import React, { useState, useEffect, useRef } from "react";
import Modal from "@/components/modal";
import API from "@/services/api";
import toast from "react-hot-toast";
import { useComponentTranslation } from "@/services/i18n";

const translations = {
  en: {
    title: "Conversation Messages",
    no_messages: "No messages in this conversation",
    loading: "Loading messages...",
    tabs: {
      messages: "Messages",
      info: "Information",
      raw: "Raw Data",
    },
    info: {
      participants: "Participants",
      job: "Job",
      organization: "Organization",
      application: "Application ID",
      status: "Status",
      created: "Created",
      updated: "Last Updated",
    },
    message_types: {
      text: "Text",
      template: "Template",
      file: "File",
      system: "System",
    },
    message_status: {
      sending: "Sending",
      sent: "Sent",
      delivered: "Delivered",
      read: "Read",
      failed: "Failed",
    },
  },
  fr: {
    title: "Messages de conversation",
    no_messages: "Aucun message dans cette conversation",
    loading: "Chargement des messages...",
    tabs: {
      messages: "Messages",
      info: "Informations",
      raw: "Données brutes",
    },
    info: {
      participants: "Participants",
      job: "Emploi",
      organization: "Organisation",
      application: "ID de candidature",
      status: "Statut",
      created: "Créé",
      updated: "Dernière mise à jour",
    },
    message_types: {
      text: "Texte",
      template: "Modèle",
      file: "Fichier",
      system: "Système",
    },
    message_status: {
      sending: "Envoi",
      sent: "Envoyé",
      delivered: "Délivré",
      read: "Lu",
      failed: "Échoué",
    },
  },
};

export default function MessagesModal({ isOpen, onClose, conversation }) {
  const [activeTab, setActiveTab] = useState("messages");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const { t } = useComponentTranslation(translations);

  useEffect(() => {
    if (conversation && activeTab === "messages") {
      fetchMessages();
    }
  }, [conversation, activeTab]);

  useEffect(() => {
    // Scroll to bottom when messages load
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchMessages = async () => {
    if (!conversation) return;
    const conversationId = conversation._id || conversation.id;
    if (!conversationId) return;

    try {
      setLoading(true);
      const response = await API.post("/message/search", {
        conversation_id: conversationId,
        limit: 100,
      });

      if (!response.ok) throw response;
      setMessages(response.data || []);
    } catch (e) {
      console.error("Error fetching messages:", e);
      toast.error(e.code || "Error fetching messages");
    } finally {
      setLoading(false);
    }
  };

  const getMessageTypeColor = (type) => {
    const colors = {
      text: "bg-blue-100 text-blue-800",
      template: "bg-purple-100 text-purple-800",
      file: "bg-green-100 text-green-800",
      system: "bg-gray-100 text-gray-800",
    };
    return colors[type] || colors.text;
  };

  const getMessageStatusColor = (status) => {
    const colors = {
      sending: "bg-yellow-100 text-yellow-800",
      sent: "bg-blue-100 text-blue-800",
      delivered: "bg-green-100 text-green-800",
      read: "bg-purple-100 text-purple-800",
      failed: "bg-red-100 text-red-800",
    };
    return colors[status] || colors.sent;
  };

  if (!conversation) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="max-w-5xl w-full">
      <div className="p-6 h-[80vh] flex flex-col">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">{t("title")}</h2>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-4">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab("messages")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "messages"
                  ? "border-purple-500 text-purple-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {t("tabs.messages")}
            </button>
            <button
              onClick={() => setActiveTab("info")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "info"
                  ? "border-purple-500 text-purple-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {t("tabs.info")}
            </button>
            <button
              onClick={() => setActiveTab("raw")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "raw"
                  ? "border-purple-500 text-purple-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {t("tabs.raw")}
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-hidden">
          {activeTab === "raw" ? (
            <pre className="bg-gray-50 p-4 rounded-lg overflow-auto text-sm h-full">
              {JSON.stringify({ conversation, messages }, null, 2)}
            </pre>
          ) : activeTab === "info" ? (
            <div className="bg-white p-4 rounded-lg border border-gray-200 overflow-auto h-full">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t("info.participants")}</label>
                  <div className="space-y-2">
                    {conversation.participants?.map((p, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                        {p.user_avatar && (
                          <img src={p.user_avatar} alt={p.user_name} className="w-8 h-8 rounded-full" />
                        )}
                        <div>
                          <div className="text-sm font-medium">{p.user_name || "Unknown"}</div>
                          <div className="text-xs text-gray-500">
                            {p.role} • Unread: {p.unread_count || 0}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t("info.job")}</label>
                  <div className="text-sm text-gray-900">{conversation.job_title || "-"}</div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t("info.organization")}</label>
                  <div className="text-sm text-gray-900">{conversation.organization_name || "-"}</div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t("info.application")}</label>
                  <div className="text-sm text-gray-900 font-mono">
                    {conversation.application_id?.toString() || "-"}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t("info.status")}</label>
                  <div className="text-sm">
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                        conversation.status === "active"
                          ? "bg-green-100 text-green-800"
                          : conversation.status === "archived"
                          ? "bg-gray-100 text-gray-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {conversation.status}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t("info.created")}</label>
                  <div className="text-sm text-gray-900">
                    {conversation.created_at ? new Date(conversation.created_at).toLocaleString("fr-FR") : "-"}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t("info.updated")}</label>
                  <div className="text-sm text-gray-900">
                    {conversation.updated_at ? new Date(conversation.updated_at).toLocaleString("fr-FR") : "-"}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col h-full">
              {loading ? (
                <div className="flex items-center justify-center flex-1">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  <span className="ml-3 text-gray-600">{t("loading")}</span>
                </div>
              ) : messages.length === 0 ? (
                <div className="flex items-center justify-center flex-1">
                  <p className="text-gray-500">{t("no_messages")}</p>
                </div>
              ) : (
                <div className="flex-1 overflow-y-auto bg-gray-50 rounded-lg p-4 space-y-3">
                  {messages.map((message) => {
                    const messageId = message._id || message.id;
                    const isSystemMessage = message.type === "system";

                    return (
                      <div
                        key={messageId}
                        className={`flex ${isSystemMessage ? "justify-center" : "justify-start"}`}
                      >
                        <div
                          className={`max-w-[70%] ${
                            isSystemMessage
                              ? "bg-gray-200 text-gray-700 rounded-full px-4 py-1 text-xs"
                              : "bg-white border border-gray-200 rounded-lg p-3 shadow-sm"
                          }`}
                        >
                          {!isSystemMessage && (
                            <div className="flex items-center gap-2 mb-2">
                              {message.sender_avatar && (
                                <img
                                  src={message.sender_avatar}
                                  alt={message.sender_name}
                                  className="w-6 h-6 rounded-full"
                                />
                              )}
                              <span className="text-xs font-semibold text-gray-900">
                                {message.sender_name || "Unknown"}
                              </span>
                              <span
                                className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${getMessageTypeColor(
                                  message.type
                                )}`}
                              >
                                {t(`message_types.${message.type}`) || message.type}
                              </span>
                              <span
                                className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${getMessageStatusColor(
                                  message.status
                                )}`}
                              >
                                {t(`message_status.${message.status}`) || message.status}
                              </span>
                            </div>
                          )}

                          <div className="text-sm text-gray-900">
                            {message.type === "file" && message.file_url ? (
                              <div>
                                <a
                                  href={message.file_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-purple-600 hover:text-purple-700 underline"
                                >
                                  📎 {message.file_name || "Download file"}
                                </a>
                                {message.content && <div className="mt-1">{message.content}</div>}
                              </div>
                            ) : (
                              <div className="whitespace-pre-wrap">{message.content}</div>
                            )}
                          </div>

                          {!isSystemMessage && (
                            <div className="mt-2 text-xs text-gray-500">
                              {message.created_at
                                ? new Date(message.created_at).toLocaleString("fr-FR")
                                : "-"}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

